import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowRightRounded from "@mui/icons-material/KeyboardArrowRightRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import { GetTwoMinLateEmployees } from "../../../../src/services/time-attendance/index";

const localDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(date.getDate()).padStart(2, "0")}`;
const initialRange = () => {
  const now = new Date();
  return {
    startDate: localDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    endDate: localDate(now),
    searchKeyword: "",
  };
};
const displayDate = (value) => {
  if (!value) return "-";
  const [year, month, day] = String(value).slice(0, 10).split("-");
  return year && month && day ? `${day}-${month}-${year}` : String(value);
};
const numberText = (value) =>
  Number(value ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const field = (row, camel, pascal, fallback = null) =>
  row?.[camel] ?? row?.[pascal] ?? fallback;
const unwrap = (response) => response?.data?.data ?? response?.data ?? {};
const errorText = (error) =>
  error?.response?.data?.message ??
  error?.response?.data?.Message ??
  error?.message ??
  "Unable to load attendance records.";
const normalizeTime = (value) => {
  if (!value) return "-";
  const text = String(value);
  return text.includes("T") ? text.split("T")[1].slice(0, 8) : text.slice(0, 8);
};

const minutesAfterCutoff = (row) => {
  const value = field(row, "lateMinutes", "LateMinutes");

  if (value === null || value === undefined || value === "") {
    return null;
  }

  const minutes = Number(value);

  if (!Number.isFinite(minutes)) {
    return null;
  }

  // Red only when check-in is strictly after the grace-period cutoff.
  return minutes > 0 ? minutes : minutes;
};

const TwoMinLateDashboard = () => {
  const [draft, setDraft] = useState(initialRange);
  const [filters, setFilters] = useState(initialRange);
  const [employees, setEmployees] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [expandedId, setExpandedId] = useState(null);
  const [detailsById, setDetailsById] = useState({});
  const [loadingDetailsId, setLoadingDetailsId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detailError, setDetailError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    setError("");
    setExpandedId(null);
    setDetailsById({});
    setDetailError("");
    try {
      const response = await GetTwoMinLateEmployees({
        StartDate: filters.startDate,
        EndDate: filters.endDate,
        SearchKeyword: filters.searchKeyword || undefined,
        Start: page * rowsPerPage,
        Length: rowsPerPage,
        PersonId: null,
      });
      const data = unwrap(response);
      setEmployees(data.records ?? data.Records ?? []);
      setTotalRecords(Number(data.totalRecords ?? data.TotalRecords ?? 0));
    } catch (e) {
      setEmployees([]);
      setTotalRecords(0);
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage, reloadKey]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const toggleDetails = async (employee) => {
    const personId = field(employee, "personId", "PersonId");
    if (personId == null) {
      setDetailError("Missing employee ID.");
      return;
    }
    if (expandedId === personId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(personId);
    setDetailError("");
    if (Object.prototype.hasOwnProperty.call(detailsById, personId)) return;
    setLoadingDetailsId(personId);
    try {
      const response = await GetTwoMinLateEmployees({
        StartDate: filters.startDate,
        EndDate: filters.endDate,
        Start: 0,
        Length: 100,
        PersonId: personId,
      });
      const data = unwrap(response);
      setDetailsById((previous) => ({
        ...previous,
        [personId]: data.details ?? data.Details ?? [],
      }));
    } catch (e) {
      setDetailError(errorText(e));
    } finally {
      setLoadingDetailsId(null);
    }
  };

  const applyFilters = () => {
    if (!draft.startDate || !draft.endDate || draft.startDate > draft.endDate) {
      setError("Select a valid date range.");
      return;
    }
    setPage(0);
    setFilters({ ...draft, searchKeyword: draft.searchKeyword.trim() });
    setReloadKey((value) => value + 1);
  };
  const resetFilters = () => {
    const defaults = initialRange();
    setDraft(defaults);
    setFilters(defaults);
    setPage(0);
    setReloadKey((value) => value + 1);
  };
  const refresh = () => setReloadKey((value) => value + 1);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1600, mx: "auto" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Office Employee Late Attendance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Office Employee: 08:02 AM cutoff | Office Employee Abad: 09:02 AM
            cutoff
          </Typography>
        </Box>
        <Chip
          label={`${numberText(totalRecords)} employees with late arrivals`}
          color="warning"
          variant="outlined"
        />
      </Stack>

      <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
            Filters
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "1fr 1fr 1.5fr auto",
              },
              gap: 1.5,
              alignItems: "center",
            }}
          >
            <TextField
              size="small"
              type="date"
              label="Start date"
              InputLabelProps={{ shrink: true }}
              value={draft.startDate}
              onChange={(e) =>
                setDraft((d) => ({ ...d, startDate: e.target.value }))
              }
            />
            <TextField
              size="small"
              type="date"
              label="End date"
              InputLabelProps={{ shrink: true }}
              value={draft.endDate}
              onChange={(e) =>
                setDraft((d) => ({ ...d, endDate: e.target.value }))
              }
            />
            <TextField
              size="small"
              placeholder="Search employee name or ID"
              value={draft.searchKeyword}
              onChange={(e) =>
                setDraft((d) => ({ ...d, searchKeyword: e.target.value }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") applyFilters();
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={applyFilters}
                disabled={loading}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={resetFilters}
                disabled={loading}
              >
                Reset
              </Button>
              <Button
                variant="text"
                startIcon={<RefreshRounded />}
                onClick={refresh}
                disabled={loading}
              >
                Refresh
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={800}>
            Employee Attendance
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Expand an employee to see the actual check-in time and the minutes
            relative to their grace-period cutoff.
          </Typography>
          <TableContainer
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell width={55} />
                  <TableCell sx={{ fontWeight: 800 }}>Employee ID</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Employee Name</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                    Late Occurrences
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={25} />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && employees.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                      No qualifying late arrivals found.
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  employees.map((employee, index) => {
                    const personId = field(employee, "personId", "PersonId");
                    const name = field(
                      employee,
                      "employeeName",
                      "EmployeeName",
                      "-",
                    );
                    const count = Number(
                      field(
                        employee,
                        "lateOccurrenceCount",
                        "LateOccurrenceCount",
                        0,
                      ),
                    );
                    const expanded = expandedId === personId;
                    const details = detailsById[personId] ?? [];
                    return (
                      <React.Fragment key={personId ?? index}>
                        <TableRow
                          hover
                          onClick={() => toggleDetails(employee)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>
                            <IconButton
                              size="small"
                              aria-label="Toggle attendance details"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDetails(employee);
                              }}
                            >
                              {expanded ? (
                                <KeyboardArrowDownRounded />
                              ) : (
                                <KeyboardArrowRightRounded />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>{personId ?? "-"}</TableCell>
                          <TableCell>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              useFlexGap
                              flexWrap="wrap"
                            >
                              <Typography variant="body2" fontWeight={750}>
                                {name}
                              </Typography>
                              {count > 0 && (
                                <Chip
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  label={`${count} late ${
                                    count === 1 ? "day" : "days"
                                  }`}
                                />
                              )}
                            </Stack>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Click to view attendance history
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              size="small"
                              color={count >= 2 ? "error" : "warning"}
                              variant="outlined"
                              label={`${count} ${
                                count === 1 ? "occurrence" : "occurrences"
                              }`}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            sx={{
                              p: 0,
                              borderBottom: expanded ? undefined : 0,
                            }}
                          >
                            <Collapse
                              in={expanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box
                                sx={{
                                  p: { xs: 1.5, md: 3 },
                                  bgcolor: "action.hover",
                                }}
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  sx={{ mb: 2 }}
                                >
                                  <Typography fontWeight={800}>
                                    Attendance History: {name}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`${details.length} records loaded`}
                                  />
                                </Stack>
                                {detailError && (
                                  <Alert severity="error" sx={{ mb: 2 }}>
                                    {detailError}
                                  </Alert>
                                )}
                                {loadingDetailsId === personId ? (
                                  <CircularProgress size={24} />
                                ) : details.length === 0 ? (
                                  <Typography color="text.secondary">
                                    No attendance details returned.
                                  </Typography>
                                ) : (
                                  <TableContainer
                                    sx={{
                                      bgcolor: "background.paper",
                                      borderRadius: 2,
                                      border: "1px solid",
                                      borderColor: "divider",
                                    }}
                                  >
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow
                                          sx={{ bgcolor: "action.hover" }}
                                        >
                                          <TableCell sx={{ fontWeight: 800 }}>
                                            Date
                                          </TableCell>
                                          <TableCell sx={{ fontWeight: 800 }}>
                                            Check-in Time
                                          </TableCell>
                                          <TableCell
                                            align="right"
                                            sx={{ fontWeight: 800 }}
                                          >
                                            Minutes After Grace Period
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {details.map((detail, i) => {
                                          const date = field(
                                            detail,
                                            "attendanceDate",
                                            "AttendanceDate",
                                          );
                                          const checkIn = normalizeTime(
                                            field(
                                              detail,
                                              "checkInTime",
                                              "CheckInTime",
                                            ),
                                          );
                                          const minutes =
                                            minutesAfterCutoff(detail);
                                          const late =
                                            minutes !== null && minutes > 0;
                                          return (
                                            <TableRow
                                              key={`${date}-${i}`}
                                              hover
                                            >
                                              <TableCell>
                                                {displayDate(date)}
                                              </TableCell>
                                              <TableCell>
                                                <Typography
                                                  variant="body2"
                                                  fontWeight={750}
                                                >
                                                  {checkIn}
                                                </Typography>
                                              </TableCell>

                                              <TableCell align="right">
                                                <Chip
                                                  size="small"
                                                  variant="outlined"
                                                  color={
                                                    minutes === null
                                                      ? "default"
                                                      : late
                                                      ? "error"
                                                      : "success"
                                                  }
                                                  label={
                                                    minutes === null
                                                      ? "Unknown"
                                                      : late
                                                      ? `${numberText(
                                                          minutes,
                                                        )} min late`
                                                      : `${checkIn}`
                                                  }
                                                  sx={{ fontWeight: 750 }}
                                                />
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            onPageChange={(_, value) => setPage(value)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default TwoMinLateDashboard;
